import { Banner, Chart, Select, Tabs } from '@cloudflare/kumo';
import * as echarts from 'echarts';
import { useEffect, useMemo, useState } from 'react';
import { DataTable as Table } from '../components/ui/DataTable';
import { PageLayout } from '../components/ui/PageLayout';
import { useAuthenticatedFetch } from '../hooks/useAuthenticatedFetch';
import { SkeletonText } from '../components/ui/Skeleton';

/**
 * Métricas da organização a partir dos eventos de produto (ProductEvent):
 * indicadores do período com a variação contra o período anterior, a série
 * diária, o funil de cada pipeline e a retenção dos contatos por coorte.
 *
 * Os dados vêm de /api/analytics/events/* (recurso `advanced_reports`).
 */

type OverviewEvent = 'contact.created' | 'conversation.started' | 'message.received' | 'conversation.handoff' | 'deal.created' | 'deal.won';

interface Overview {
  kpis: Array<{ event: OverviewEvent; count: number; previous: number; deltaPct: number | null }>;
  revenueWon: { amount: number; previous: number; deltaPct: number | null };
  daily: Array<{ day: string } & Record<OverviewEvent, number>>;
}

interface Funnel {
  pipelineId: string;
  pipelineName: string;
  stages: Array<{ stageId: string; stageName: string; dealsCount: number; reachedCount: number; totalAmount: number; conversionRate: number }>;
  totalDeals: number;
  overallWonCount: number;
  overallLostCount: number;
  wonAmount: number;
  winRate: number;
  overallConversionRate: number;
}

interface CohortRow { cohort: string; total: number; retained: Record<string, number | null> }

const LABELS: Record<OverviewEvent, string> = {
  'contact.created': 'Contatos novos',
  'conversation.started': 'Conversas iniciadas',
  'message.received': 'Mensagens recebidas',
  'conversation.handoff': 'Passadas para a equipe',
  'deal.created': 'Negócios criados',
  'deal.won': 'Negócios ganhos',
};

// Séries do gráfico: o que mostra o volume de atendimento
const CHART_EVENTS: OverviewEvent[] = ['conversation.started', 'contact.created', 'conversation.handoff'];

const PERIODS = [
  { value: '7', label: 'Últimos 7 dias' },
  { value: '30', label: 'Últimos 30 dias' },
  { value: '90', label: 'Últimos 90 dias' },
];

const DAY_MS = 86_400_000;
const num = (v: number) => v.toLocaleString('pt-BR');
const brl = (v: number) => v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 });
const dayLabel = (iso: string) => new Date(`${iso}T12:00:00`).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });

// Indicadores em que cair é bom (menos conversas precisando da equipe)
const LOWER_IS_BETTER: ReadonlySet<string> = new Set<OverviewEvent>(['conversation.handoff']);

function Card({ title, description, actions, children }: { title: React.ReactNode; description?: React.ReactNode; actions?: React.ReactNode; children: React.ReactNode }) {
  return (
    <section className="rounded-xl border border-kumo-line bg-kumo-base p-5">
      <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h2 className="m-0 text-lg font-semibold">{title}</h2>
          {description && <p className="m-0 mt-1 text-sm text-kumo-subtle">{description}</p>}
        </div>
        {actions}
      </div>
      {children}
    </section>
  );
}

function Grid({ min, children }: { min: number; children: React.ReactNode }) {
  return <div className="grid gap-4" style={{ gridTemplateColumns: `repeat(auto-fit, minmax(${min}px, 1fr))` }}>{children}</div>;
}

function Bar({ label, value, info }: { label: string; value: number; info: string }) {
  return (
    <div role="progressbar" aria-label={label} aria-valuenow={value} aria-valuemin={0} aria-valuemax={100}>
      <div className="flex items-baseline justify-between gap-3 text-sm"><span className="font-medium">{label}</span><span className="text-kumo-subtle">{value}%</span></div>
      <div className="mt-1 h-2 overflow-hidden rounded-full bg-kumo-fill"><div className="h-full rounded-full bg-kumo-brand" style={{ width: `${value}%` }} /></div>
      <div className="mt-1 text-xs text-kumo-subtle">{info}</div>
    </div>
  );
}

function Delta({ pct, lowerIsBetter = false }: { pct: number | null; lowerIsBetter?: boolean }) {
  if (pct === null) return <span className="text-xs text-kumo-inactive block">sem base no período anterior</span>;
  const good = lowerIsBetter ? pct < 0 : pct > 0;
  const sign = pct > 0 ? '+' : '';
  return <span className={`text-xs block ${pct === 0 ? 'text-kumo-subtle' : good ? 'text-kumo-success' : 'text-kumo-danger'}`}>{`${sign}${pct}% vs. período anterior`}</span>;
}

function Kpi({ label, value, pct, testId, lowerIsBetter }: { label: string; value: string; pct: number | null; testId: string; lowerIsBetter?: boolean }) {
  return (
    <div data-testid={testId}>
      <span className="text-xs text-kumo-subtle">{label}</span>
      <div className="text-2xl font-semibold">{value}</div>
      <Delta pct={pct} lowerIsBetter={lowerIsBetter} />
    </div>
  );
}

async function getData<T>(res: Response): Promise<T | { error: string }> {
  const body = await res.json().catch(() => ({}));
  if (!res.ok) return { error: (body as { message?: string }).message || 'Não foi possível carregar' };
  return (body as { data: T }).data;
}

export default function MetricasPage() {
  const fetchApi = useAuthenticatedFetch();
  const [days, setDays] = useState(30);
  const [overview, setOverview] = useState<Overview | { error: string } | null>(null);
  const [funnels, setFunnels] = useState<Funnel[] | { error: string } | null>(null);
  const [granularity, setGranularity] = useState<'week' | 'month'>('week');
  const [cohorts, setCohorts] = useState<CohortRow[] | { error: string } | null>(null);
  const [pipelineId, setPipelineId] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    setOverview(null);
    const end = new Date();
    const start = new Date(end.getTime() - days * DAY_MS);
    const qs = new URLSearchParams({ startDate: start.toISOString(), endDate: end.toISOString() });
    fetchApi(`/api/analytics/events/overview?${qs}`)
      .then((r) => getData<Overview>(r))
      .then((d) => { if (alive) setOverview(d); })
      .catch(() => { if (alive) setOverview({ error: 'Sem conexão com o servidor' }); });
    return () => { alive = false; };
  }, [days, fetchApi]);

  useEffect(() => {
    let alive = true;
    fetchApi('/api/analytics/events/funnel')
      .then((r) => getData<Funnel[]>(r))
      .then((d) => { if (alive) setFunnels(d); })
      .catch(() => { if (alive) setFunnels({ error: 'Sem conexão com o servidor' }); });
    return () => { alive = false; };
  }, [fetchApi]);

  useEffect(() => {
    let alive = true;
    setCohorts(null);
    fetchApi(`/api/analytics/events/cohort?granularity=${granularity}&periods=8`)
      .then((r) => getData<CohortRow[]>(r))
      .then((d) => { if (alive) setCohorts(d); })
      .catch(() => { if (alive) setCohorts({ error: 'Sem conexão com o servidor' }); });
    return () => { alive = false; };
  }, [granularity, fetchApi]);

  const series = useMemo(() => {
    if (!overview || 'error' in overview) return [];
    return CHART_EVENTS.map((e) => ({
      title: LABELS[e],
      type: 'line' as const,
      data: overview.daily.map((d) => ({ x: new Date(`${d.day}T12:00:00`), y: d[e] })),
    }));
  }, [overview]);

  const funnelList = funnels && !('error' in funnels) ? funnels : [];
  const funnel = funnelList.find((f) => f.pipelineId === pipelineId) ?? funnelList[0];
  const cohortKeys = cohorts && !('error' in cohorts) ? Object.keys(cohorts[0]?.retained ?? {}) : [];
  const periodOption = PERIODS.find((p) => p.value === String(days)) ?? PERIODS[1];

  return (
    <PageLayout
      title="Métricas"
      description="Contatos, conversas e vendas da organização, com a comparação contra o período anterior. Os números contam a partir de quando cada evento começou a ser registrado."
      actions={
        <Select value={(periodOption)?.value ?? null} items={PERIODS} onValueChange={(value: string | null) => { const v = value; if (v) setDays(Number(v)); }} aria-label="Período" data-testid="metricas-periodo" />
      }
    >
      <div className="flex flex-col gap-5">
        <Card title="Resumo do período">
          {!overview && <SkeletonText lines={3} label="Carregando métricas" />}
          {overview && 'error' in overview && <Banner variant="error" description={<>{overview.error}</>} />}
          {overview && !('error' in overview) && (
            <Grid min={170}>
              {overview.kpis.map((k) => (
                <Kpi key={k.event} testId={`kpi-${k.event}`} label={LABELS[k.event]} value={num(k.count)} pct={k.deltaPct} lowerIsBetter={LOWER_IS_BETTER.has(k.event)} />
              ))}
              <Kpi testId="kpi-receita" label="Receita ganha" value={brl(overview.revenueWon.amount)} pct={overview.revenueWon.deltaPct} />
            </Grid>
          )}
        </Card>

        <Card title="Atendimento" description="Por dia, no período escolhido">
          {!overview && <SkeletonText lines={3} label="Carregando métricas" />}
          {overview && 'error' in overview && <Banner variant="error" description={<>Não foi possível carregar o gráfico</>} />}
          {overview && !('error' in overview) && (series[0]?.data.length ? (
            <Chart
              echarts={echarts}
              height={260}
              isDarkMode={document.documentElement.dataset.mode === 'dark'}
              options={{
                tooltip: { trigger: 'axis' },
                legend: { bottom: 0 },
                grid: { left: 40, right: 16, top: 16, bottom: 40 },
                xAxis: { type: 'category', data: series[0].data.map((d) => dayLabel(d.x.toISOString().slice(0, 10))) },
                yAxis: { type: 'value', minInterval: 1 },
                series: series.map((sr) => ({ name: sr.title, type: 'line', smooth: true, data: sr.data.map((d) => d.y) })),
              }}
            />
          ) : <div className="text-center text-kumo-inactive">Nenhum evento no período.</div>)}
        </Card>

        <Card
          title={`Funil de vendas${funnel && funnelList.length > 1 ? '' : funnel ? ` · ${funnel.pipelineName}` : ''}`}
          description="Pipelines do projeto ativo. Quantos negócios chegaram a cada etapa (os ganhos passaram por todas); a conversão compara com a etapa anterior."
          actions={funnelList.length > 1 && funnel ? (
            <Select value={funnel.pipelineId} items={funnelList.map((f) => ({ value: f.pipelineId, label: f.pipelineName }))} onValueChange={(value: string | null) => setPipelineId(value ?? null)} aria-label="Pipeline" />
          ) : undefined}
        >
          {!funnels && <SkeletonText lines={3} label="Carregando métricas" />}
          {funnels && 'error' in funnels && <Banner variant="error" description={<>{funnels.error}</>} />}
          {funnels && !('error' in funnels) && !funnel && <div className="text-kumo-inactive">Nenhum pipeline ainda. Crie um em Vendas › Funil de vendas.</div>}
          {funnel && (
            <div className="flex flex-col gap-4">
              <Grid min={150}>
                <div><span className="text-xs text-kumo-subtle">Negócios</span><div className="text-2xl font-semibold">{num(funnel.totalDeals)}</div></div>
                <div><span className="text-xs text-kumo-subtle">Ganhos</span><div className="text-2xl font-semibold">{num(funnel.overallWonCount)}</div><span className="text-xs text-kumo-inactive block">{brl(funnel.wonAmount)}</span></div>
                <div><span className="text-xs text-kumo-subtle">Taxa de fechamento</span><div className="text-2xl font-semibold">{funnel.overallWonCount + funnel.overallLostCount ? `${funnel.winRate}%` : '—'}</div><span className="text-xs text-kumo-inactive block">ganhos ÷ (ganhos + perdidos)</span></div>
                <div><span className="text-xs text-kumo-subtle">Conversão geral</span><div className="text-2xl font-semibold">{funnel.totalDeals ? `${funnel.overallConversionRate}%` : '—'}</div><span className="text-xs text-kumo-inactive block">ganhos ÷ todos os negócios</span></div>
              </Grid>
              {funnel.stages.length === 0
                ? <div className="text-kumo-inactive">Este pipeline não tem etapas abertas.</div>
                : funnel.stages.map((s, i) => {
                  const top = funnel.stages[0].reachedCount;
                  return (
                    <Bar
                      key={s.stageId}
                      label={s.stageName}
                      value={top ? Math.round((s.reachedCount / top) * 100) : 0}
                      info={`${num(s.reachedCount)} chegaram · ${num(s.dealsCount)} parados aqui (${brl(s.totalAmount)})${i > 0 ? ` · ${s.conversionRate}% da etapa anterior` : ''}`}
                    />
                  );
                })}
            </div>
          )}
        </Card>

        <Card
          title="Retenção de contatos"
          description="Contatos que entraram em cada período e quantos deles voltaram a mandar mensagem nos períodos seguintes."
          actions={<Tabs value={granularity} onValueChange={(v: string) => setGranularity(v as 'week' | 'month')} tabs={[{ value: 'week', label: 'Semana' }, { value: 'month', label: 'Mês' }]} />}
        >
        <Table
          loading={!cohorts}
          loadingText="Carregando…"
          items={cohorts && !('error' in cohorts) ? [...cohorts].reverse() : []}
          empty={<div className="text-kumo-inactive">{cohorts && 'error' in cohorts ? cohorts.error : 'Nenhum contato novo no período.'}</div>}
          columnDefinitions={[
            { id: 'cohort', header: granularity === 'week' ? 'Semana de' : 'Mês', cell: (r) => (granularity === 'week' ? new Date(`${r.cohort}T12:00:00`).toLocaleDateString('pt-BR') : new Date(`${r.cohort}T12:00:00`).toLocaleDateString('pt-BR', { month: 'short', year: 'numeric' })) },
            { id: 'total', header: 'Contatos', cell: (r) => num(r.total) },
            ...cohortKeys.map((k, i) => ({
              id: k,
              header: granularity === 'week' ? `${i + 1}ª semana depois` : `${i + 1}º mês depois`,
              cell: (r: CohortRow) => (r.total === 0 || r.retained[k] === null ? '—' : `${r.retained[k]}%`),
            })),
          ]}
        />
        </Card>
      </div>
    </PageLayout>
  );
}
